import type { InspectionCategory, InspectionType } from '../types';

// ============================================================
// Check definitions — tagged by which inspection types include them
// ============================================================

export interface CheckDef {
  id: string;
  category: string;
  label: string;
  required: boolean;
  types: InspectionType[]; // which inspection types include this check
}

export const ALL_CHECKS: CheckDef[] = [
  // ── Exterior & Body ──────────────────────────────────────
  { id: 'ext-body', category: 'exterior', label: 'Body condition (no major dents, damage)', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'ext-paint', category: 'exterior', label: 'Paint / decals / Wire3 branding intact', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'ext-mirrors', category: 'exterior', label: 'Mirrors (both sides, no cracks)', required: true, types: ['weekly', 'monthly'] },
  { id: 'ext-windows', category: 'exterior', label: 'Windshield & windows (no cracks >6")', required: true, types: ['weekly', 'monthly'] },
  { id: 'ext-wipers', category: 'exterior', label: 'Wiper blades functional', required: true, types: ['weekly', 'monthly'] },
  { id: 'ext-door-locks', category: 'exterior', label: 'Doors, handles, and locks', required: true, types: ['daily', 'weekly', 'monthly'] },

  // ── Lights & Signals ─────────────────────────────────────
  { id: 'lt-headlights', category: 'lights', label: 'Headlights (low & high beam)', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-tail', category: 'lights', label: 'Tail lights', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-brake', category: 'lights', label: 'Brake lights', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-turn', category: 'lights', label: 'Turn signals (front & rear)', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-hazard', category: 'lights', label: 'Hazard / emergency flashers', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-reverse', category: 'lights', label: 'Reverse / backup lights', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'lt-reflectors', category: 'lights', label: 'DOT reflectors / conspicuity tape', required: false, types: ['daily', 'weekly', 'monthly'] },

  // ── Tires & Wheels ───────────────────────────────────────
  { id: 'tr-lf', category: 'tires', label: 'Left front tire — tread & condition', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'tr-rf', category: 'tires', label: 'Right front tire — tread & condition', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'tr-lr', category: 'tires', label: 'Left rear tire — tread & condition', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'tr-rr', category: 'tires', label: 'Right rear tire — tread & condition', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'tr-lugs', category: 'tires', label: 'Lug nuts tight, no missing', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'tr-pressure', category: 'tires', label: 'Tire pressure within spec', required: true, types: ['weekly', 'monthly'] },
  { id: 'tr-spare', category: 'tires', label: 'Spare tire present & inflated', required: false, types: ['monthly'] },

  // ── Fluids & Engine ──────────────────────────────────────
  { id: 'fl-oil', category: 'fluids', label: 'Engine oil level', required: true, types: ['monthly'] },
  { id: 'fl-coolant', category: 'fluids', label: 'Coolant level', required: false, types: ['monthly'] },
  { id: 'fl-brake', category: 'fluids', label: 'Brake fluid level', required: false, types: ['monthly'] },
  { id: 'fl-trans', category: 'fluids', label: 'Transmission fluid', required: false, types: ['monthly'] },
  { id: 'fl-washer', category: 'fluids', label: 'Windshield washer fluid', required: true, types: ['monthly'] },
  { id: 'fl-leaks', category: 'fluids', label: 'No visible fluid leaks under vehicle', required: true, types: ['weekly', 'monthly'] },
  { id: 'fl-belts', category: 'fluids', label: 'Belts & hoses (no cracks, wear)', required: true, types: ['weekly', 'monthly'] },

  // ── Brakes & Steering ────────────────────────────────────
  { id: 'br-pedal', category: 'brakes', label: 'Brake pedal firm, no spongy feel', required: true, types: ['weekly', 'monthly'] },
  { id: 'br-parking', category: 'brakes', label: 'Parking / emergency brake holds', required: true, types: ['weekly', 'monthly'] },
  { id: 'br-noise', category: 'brakes', label: 'No unusual brake noise or vibration', required: true, types: ['weekly', 'monthly'] },
  { id: 'st-play', category: 'brakes', label: 'Steering — no excessive play', required: true, types: ['weekly', 'monthly'] },
  { id: 'st-power', category: 'brakes', label: 'Power steering functional', required: true, types: ['weekly', 'monthly'] },

  // ── Interior & Cab ───────────────────────────────────────
  { id: 'int-seat', category: 'interior', label: 'Seat & seatbelt functional', required: true, types: ['weekly', 'monthly'] },
  { id: 'int-horn', category: 'interior', label: 'Horn works', required: true, types: ['weekly', 'monthly'] },
  { id: 'int-gauges', category: 'interior', label: 'Dashboard gauges / warning lights normal', required: true, types: ['weekly', 'monthly'] },
  { id: 'int-hvac', category: 'interior', label: 'HVAC / defroster working', required: true, types: ['weekly', 'monthly'] },
  { id: 'int-clean', category: 'interior', label: 'Cab clean & organized', required: false, types: ['daily', 'weekly', 'monthly'] },
  { id: 'int-reg', category: 'interior', label: 'Registration & insurance cards present', required: true, types: ['monthly'] },

  // ── Safety Equipment ─────────────────────────────────────
  { id: 'sf-extinguisher', category: 'safety', label: 'Fire extinguisher (charged, accessible)', required: true, types: ['monthly'] },
  { id: 'sf-triangles', category: 'safety', label: 'Warning triangles / flares', required: false, types: ['monthly'] },
  { id: 'sf-firstaid', category: 'safety', label: 'First aid kit', required: true, types: ['monthly'] },
  { id: 'sf-vest', category: 'safety', label: 'Hi-vis vest in cab', required: true, types: ['monthly'] },
  { id: 'sf-cones', category: 'safety', label: 'Traffic cones (if required)', required: false, types: ['monthly'] },

  // ── Work Equipment & Cargo ───────────────────────────────
  { id: 'eq-ladder', category: 'equipment', label: 'Ladder secured properly', required: false, types: ['daily', 'weekly', 'monthly'] },
  { id: 'eq-tools', category: 'equipment', label: 'Tools & equipment secured', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'eq-cargo', category: 'equipment', label: 'Cargo area organized, nothing loose', required: true, types: ['daily', 'weekly', 'monthly'] },
  { id: 'eq-fiber', category: 'equipment', label: 'Fiber spools / materials secured', required: false, types: ['daily', 'weekly', 'monthly'] },
  { id: 'eq-racks', category: 'equipment', label: 'Roof rack / conduit rack secure', required: false, types: ['daily', 'weekly', 'monthly'] },
];

// Category metadata
const CATEGORY_META: Record<string, { name: string; icon: string }> = {
  exterior: { name: 'Exterior & Body', icon: 'Car' },
  lights:   { name: 'Lights & Signals', icon: 'Lightbulb' },
  tires:    { name: 'Tires & Wheels', icon: 'Circle' },
  fluids:   { name: 'Fluids & Engine', icon: 'Droplets' },
  brakes:   { name: 'Brakes & Steering', icon: 'ShieldAlert' },
  interior: { name: 'Interior & Cab', icon: 'Armchair' },
  safety:   { name: 'Safety Equipment', icon: 'HardHat' },
  equipment: { name: 'Work Equipment & Cargo', icon: 'Wrench' },
};

// Category order
const CATEGORY_ORDER = ['exterior', 'lights', 'tires', 'fluids', 'brakes', 'interior', 'safety', 'equipment'];

export function createFreshChecklist(type: InspectionType = 'daily'): InspectionCategory[] {
  // Filter checks for this inspection type
  const relevantChecks = ALL_CHECKS.filter(c => c.types.includes(type));

  // Group by category maintaining order
  const categories: InspectionCategory[] = CATEGORY_ORDER
    .map(catId => {
      const meta = CATEGORY_META[catId];
      const checks = relevantChecks
        .filter(c => c.category === catId)
        .map(c => ({
          id: c.id,
          category: catId,
          label: c.label,
          status: null,
          notes: '',
          photos: [],
          required: c.required,
        }));

      if (checks.length === 0) return null;

      return {
        id: catId,
        name: meta.name,
        icon: meta.icon,
        checks,
      };
    })
    .filter(Boolean) as InspectionCategory[];

  return categories;
}

// Export check lists for use in manualChecks.ts and photoCheckMapper.ts
export function getCheckIdsForType(type: InspectionType): Set<string> {
  return new Set(ALL_CHECKS.filter(c => c.types.includes(type)).map(c => c.id));
}